import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { typography } from 'src/config/typography';
import { media } from 'src/config/breakpoints';
import { colors } from 'src/config/colors';
import Image from 'next/image';
import { AboutUsKnowledge } from 'src/common/models/aboutUsStaticPage';
import { prependCMSUrl } from 'src/config/cms';
import Blocks from 'editorjs-blocks-react-renderer';
import { editorjsRenderers } from 'src/config/editorjs';
import { StaticPageEditorContent } from 'src/public/components/static-page/StaticPageEditorContent';

const BulletPoints = styled.ul`
  list-style-type: none;
  margin-top: 1.25rem;
  padding-left: 0;

  li {
    font-family: ${typography.fontFamilyDefault};
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.15em;
    line-height: 1rem;
    padding-left: 1rem;
    position: relative;
    text-transform: uppercase;

    &::before {
      content: '•';
      font-size: 1rem;
      left: 0;
      position: absolute;
      text-align: center;
    }

    &:not(:first-of-type) {
      margin-top: 0.625rem;
    }
  }

  p {
    margin: 0;
  }
`;

const Container = styled.div`
  align-items: center;
  background: ${colors.blue};
  display: flex;
  flex-direction: column;
  padding: 0 0.75rem 3rem;
  width: 100%;

  ${media.w.min.px768(css`
    flex-direction: row;
    padding: 0;
  `)};
`;

const Content = styled.div`
  color: ${colors.white};
  display: flex;
  flex-direction: column;
  margin-top: 2.5rem;
  max-width: 25rem;
  padding: 0 0.75rem;

  ${media.w.min.px768(css`
    margin-top: 0;
    max-width: none;
    padding: 3.75rem 5.725rem 3rem 0;
    width: 50%;
  `)};
`;

const ImageBox = styled.div`
  align-items: center;
  display: flex;
  height: 45%;
  justify-content: center;
  padding: 2.25rem 2.5rem 0;
  width: 100%;

  ${media.w.min.px768(css`
    padding: 3.75rem 2.5rem;
    width: 50%;
    height: 100%;
  `)};

  img {
    height: 100%;
    width: 16.5rem;

    ${media.w.min.px768(css`
      height: auto;
      width: 100%;
    `)}
  }
`;

const Subtitle = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1.5rem;
  line-height: 1.875rem;
  margin-top: 1rem;
`;

const Text = styled.div`
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  line-height: 1.375rem;
  margin-top: 1.875rem;

  p {
    margin: 0 0 1.875rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const Title = styled.div`
  font-size: 0.75rem;
  font-family: ${typography.fontFamilyDefault};
  color: ${colors.pfp3};
  line-height: 1rem;
  letter-spacing: 0.15rem;
  font-weight: 600;
`;

export interface AboutUsKnowledgeSectionProps {
  content: AboutUsKnowledge;
}

export const AboutUsKnowledgeSection: FC<AboutUsKnowledgeSectionProps> = ({ content }) => (
  <Container>
    <ImageBox>
      <Image
        height={content.image.data.attributes.height}
        src={prependCMSUrl(content.image.data.attributes.url)}
        width={content.image.data.attributes.width}
      />
    </ImageBox>

    <Content>
      <Title>{content.subtitle}</Title>
      <Subtitle>{content.heading}</Subtitle>

      <Text>
        <StaticPageEditorContent>
          <Blocks data={content.text} renderers={editorjsRenderers} />
        </StaticPageEditorContent>
      </Text>

      <BulletPoints>
        {content.list.map((item) => (
          <li key={item.id}>
            <StaticPageEditorContent>
              <Blocks data={item.text} renderers={editorjsRenderers} />
            </StaticPageEditorContent>
          </li>
        ))}
      </BulletPoints>
    </Content>
  </Container>
);
